// SEQ-180 follow-up (14 September 2026): the ONE place that decides whether a
// P&L bucket's wages carry the wages multiplier.
//
// WHY THIS EXISTS. The project P&L tab and the analytics page disagreed on
// every monthly wages figure by exactly the multiplier's share (4% at the
// 1.04 default). The tab applies the multiplier only to its Total column
// (src/lib/pnl/pnlCalculationCore.ts:218-228, calculateTotalsPnl), and never
// to a month cell: calculateMonthlyPnl adds the typed wages straight into
// totalDeductions with no multiplier at all. The analytics applied it at the
// (project, month) grain and summed afterwards, so its aggregate agreed with
// the tab's Total to the cent while every monthly row was 4% of wages short
// on net profit. Josh asked for analytics to show "what team seeing on p&l";
// this module is that rule, named once, so the SQL RPC, computePnlBuckets and
// the parity tests cannot drift apart again.
//
// THE RULE. A bucket that spans exactly one calendar month shows RAW wages,
// matching a P&L tab month cell. Every other bucket aggregates over time and
// shows MULTIPLIED wages, matching the tab's Total column.
//
// The rule keys off the GRAIN, never off how much data a bucket happens to
// hold. A quarter bucket that a narrow date filter has reduced to one month
// is still an aggregate bucket and is still multiplied. That is deliberate
// and it is what the tab itself does: a P&L tab showing a single month still
// multiplies its Total column while leaving that month's cell raw. Deciding
// from the data instead would make the same bucket type behave differently
// on two screens.
//
// THE COST, STATED. After this rule, the analytics' monthly rows no longer
// sum to its own aggregate rows: the difference is exactly
// (multiplier - 1) x the wages in those months. That reverses SEQ-180 plan
// decision D2, which chose internal additivity. It is the same inconsistency
// the P&L tab has always had, and Elijah chose agreeing with the tab over
// agreeing with ourselves, because the tab is the surface the team manages
// the business from. Do not "fix" the additivity back without that decision
// being revisited: doing so silently re-opens the 4% gap Josh reported.

/**
 * Grain names whose every bucket spans exactly one calendar month.
 *
 * Deliberately a set of plain strings rather than a typed union, because it
 * spans TWO separate vocabularies that both use "month" to mean the same
 * thing: the RPC's `groupBy` (`PnlGroupBy` in analyticsClient.ts, which gained
 * `project_month` under SEQ-193) and the client rollup grain (`PnlRollupGrain`
 * in pnlAnalytics.ts, which has had it from the start). wagesMultiplierGrain.test.ts asserts every
 * member of both vocabularies is classified, which is what makes the loose
 * parameter type safe.
 */
export const SINGLE_MONTH_GRAINS: readonly string[] = ["month", "project_month"];

/**
 * True when a bucket at this grain shows wages multiplied (the tab's Total
 * column), false when it shows them raw (a tab month cell).
 */
export function wagesMultiplierAppliesAtGrain(grain: string): boolean {
  return !SINGLE_MONTH_GRAINS.includes(grain);
}

/**
 * The grain a project P&L tab MONTH COLUMN is, named so pnlCalculationCore.ts
 * and its tests can state which half of the rule above they are.
 *
 * calculateMonthlyPnl has no multiplication in it at all, so it cannot consult
 * the rule at runtime the way calculateTotalsPnl does below: there is no site
 * to guard. Inventing a multiply-by-one purely to create a symmetry would add
 * a category-classification pass to the tab's hottest pure function for no
 * behavioural gain, which is worse than leaving it declarative. What binds it
 * instead is a behavioural test (wagesMultiplierGrain.test.ts): calculateMonthlyPnl
 * output is asserted to be identical under multipliers 1.0, 1.04 and 1.5, so
 * if anyone ever adds multiplication there, that test fails.
 */
export const PNL_TAB_MONTH_GRAIN = "month";

/**
 * The grain a project P&L tab TOTAL COLUMN is: one project across the whole
 * selected range, which is exactly the analytics' own `project` grain.
 * calculateTotalsPnl resolves its multiplier through this, so the tab's Total
 * and every analytics aggregate move together by construction rather than by
 * two implementations happening to agree.
 */
export const PNL_TAB_TOTAL_GRAIN = "project";

/**
 * The currency metrics whose value is BUILT from multiplied wages, so the same
 * metric genuinely differs between a month bucket and an aggregate bucket.
 *
 * Deliberately not "every currency metric". `calculated.total_income` is
 * untouched by the multiplier and sums across months perfectly, so labelling it
 * would be noise and, worse, wrong. `labour.base_wages` and
 * `labour.extra_wages` are raw at EVERY grain and also sum fine. Only cost and
 * profit carry the multiplier through, and they are exactly the two a reader
 * would total down a column.
 */
export const WAGES_MULTIPLIER_AFFECTED_CURRENCY_METRICS: readonly string[] = [
  "calculated.total_cost",
  "calculated.net_profit",
];

/**
 * Whether a widget showing `metricKey` at `grain` should carry the raw-wages
 * note, i.e. whether a reader could total its column and get a number that
 * disagrees with an aggregate shown beside it.
 *
 * ALWAYS, never conditional on what else is on the dashboard. A conditional
 * label would make the same widget say different things on two dashboards, and
 * would vanish the moment someone removed the aggregate widget it was keyed to,
 * which is precisely when the remaining column is most likely to be summed by
 * hand. Widgets are also duplicated and drilled into, and a label that depends
 * on its neighbours does not survive either.
 */
export function showsRawWagesNote(grain: string, metricKey: string): boolean {
  return !wagesMultiplierAppliesAtGrain(grain) && WAGES_MULTIPLIER_AFFECTED_CURRENCY_METRICS.includes(metricKey);
}

/** The note itself, kept beside the rule it describes so the copy and the behaviour cannot drift apart. */
export const RAW_WAGES_NOTE = "Wages as typed, matching the project P&L month columns. These months do not sum to the total.";
